import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";

interface Response {
  question_id: number;
  question_text: string;
  answer: string;
}

interface Questionnaire {
  id: number;
  title: string;
  responses: Array<{
    id: string;
    memberId: string;
    memberCodename: string;
    responseDate: string;
  }>;
}

const QuestionnaireResponses: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResponse, setSelectedResponse] = useState<Response[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/questionnaires/responses`
      );
      setQuestionnaires(response.data);
    } catch (error) {
      console.error("Error fetching questionnaires and responses:", error);
    }
  };

  const handleViewDetails = async (
    questionnaireId: number,
    responseId: string
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/questionnaires/${questionnaireId}/responses/${responseId}`
      );
      setSelectedResponse(response.data);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error fetching response details:", error);
    }
  };

  const filteredQuestionnaires = questionnaires.map((questionnaire) => ({
    ...questionnaire,
    responses: questionnaire.responses.filter((response) =>
      response.memberCodename.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  }));

  return (
    <div>
      <TextField
        label="Search Participants"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: "20px" }}
      />
      {filteredQuestionnaires.map((questionnaire) => (
        <Accordion key={questionnaire.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{questionnaire.title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Participant</TableCell>
                    <TableCell>Response Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questionnaire.responses.map((response) => (
                    <TableRow key={response.id}>
                      <TableCell>{response.memberCodename}</TableCell>
                      <TableCell>{response.responseDate}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            handleViewDetails(questionnaire.id, response.id)
                          }
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Response Details</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Answer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedResponse.map((response) => (
                  <TableRow key={response.question_id}>
                    <TableCell>{response.question_text}</TableCell>
                    <TableCell>{response.answer}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default QuestionnaireResponses;
